import { withNamespaces } from "react-i18next";
import React, { memo, useEffect, useMemo, useState } from "react";
import { Alert, Col, Container, Row, Spinner } from "reactstrap";
import { Nav, NavItem, NavLink } from 'reactstrap';
import PageBody from "../../../../components/page-body";
import Navigator from "../../../../components/navigator";
import { useHistory, useLocation } from "react-router-dom";
import ListControls from "./pages/list-controls";
import ListVersions from "./pages/versions";
import ControlsDashboard from "./pages/dashboard";

import "./style.scss";
import { useQuery } from "@tanstack/react-query";
import ComplianceFrameworkSettingsService from "src/modules/data-protection/apis/ComplianceFrameworkSettingsService";
import { toast } from "react-toastify";
import { ControlsFrameworks } from "src/modules/data-protection/constants";

const Tabs = memo(({
    t
}) => {
    const [activeTab, setActiveTab] = useState("");
    const history = useHistory();
    const location = useLocation();

    const isActiveTab = (currentTab, tab) => currentTab === tab;

    const tabsItems = useMemo(() => {
        return [
            {
                key: "list",
                title: "Overview"
            },
            {
                key: "versions",
                title: "Versions"
            },
            {
                key: "dashboard",
                title: "Dashboard"
            }
        ];
    }, []);

    useEffect(() => {
        const tabKey = (new URLSearchParams(location.search)).get('tab');
        const tab = (tabsItems.find((tab) => tab.key === tabKey))?.key;

        if (!tab) {
            history.push({
                search: '?tab=list'
            });
        }
        else {
            setActiveTab(tab);
        }
    }, [
        location.search,
        tabsItems,
        history
    ]);

    return (
        <div style={{
            backgroundColor: '#D1D2D6',
        }}>
            <Nav tabs className="custom-style-1">
                {
                    tabsItems.map((link) => {
                        return (
                            <NavItem
                                className={isActiveTab(activeTab, link.key) ? 'active' : ''}
                                key={link.key}
                            >
                                <NavLink
                                    className={isActiveTab(activeTab, link.key) ? 'active' : ''}
                                    onClick={() => history.push({
                                        search: '?tab=' + link.key
                                    })}
                                >
                                    {t(link.title)}
                                </NavLink>
                            </NavItem>
                        )
                    })
                }
            </Nav>
        </div>
    );

});

const Controls = ({ t }) => {
    const location = useLocation();

    const navigatorOptions = useMemo(() => {
        return {
            enable: true,
            breadCrumbs: [
                {
                    title: "SIP",
                    link: `/admin/data-protection/dashboard`,
                },
                {
                    title: "Controls",
                    link: `/admin/data-protection/controls`,
                }
            ]
        }
    }, []);

    const [controlType, setControlType] = useState(null);

    const {
        data: frameworkSettings,
        isFetching: frameworkSettingsIsFetching,
        isError: frameworkSettingsFetchFaild
    } = useQuery({
        queryKey: ['data-protection-fetch-compliance-frameworks-settings'],
        queryFn: async () => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.fetchSettings();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to load framework settings. Please try again.'), {
                type: "error"
            });
        }
    });

    const ControlsFrameworksOptions = useMemo(() => {
        if (!frameworkSettings?.enabled_frameworks) return [];

        return ControlsFrameworks.filter(
            (framework) => frameworkSettings.enabled_frameworks.includes(framework.key)
        ).map((framework) => {
            return {
                value: framework.key,
                label: framework.title
            }
        })
    }, [
        frameworkSettings
    ]);

    useEffect(() => {
        if (ControlsFrameworksOptions.length > 0) setControlType(ControlsFrameworksOptions[0]);
        
    }, [ ControlsFrameworksOptions ]);

    const renderTabContent = () => {
        if (frameworkSettingsIsFetching) {
            return (
                <div>
                    <Spinner 
                        size="sm"
                        className="me-1"
                    />
                    {t('Loading')}...
                </div>
            )
        }

        if (frameworkSettingsFetchFaild) {
            return (
                <Alert
                    color="danger"
                >
                    {t('Failed to load ISMS Framework settings. Please try again later.')}
                </Alert>
            )
        }

        if (!ControlsFrameworksOptions.length || !controlType) {
            return (
                <Alert
                    color="warning"
                >
                    {t('No ISMS Frameworks are currently enabled in the system. Please enable one or more frameworks in the settings to make them available.')}
                </Alert>
            )
        }

        const searchParams = new URLSearchParams(location.search);
        
        const tab = searchParams.get("tab") || "list";

        switch (tab) {
            case "versions":
                return (
                    <ListVersions 
                        ControlsFrameworksOptions={ControlsFrameworksOptions}
                        controlType={controlType}
                        setControlType={setControlType}
                    />
                );            
            case "dashboard":
                return (
                    <ControlsDashboard
                        ControlsFrameworksOptions={ControlsFrameworksOptions}
                        controlType={controlType}
                        setControlType={setControlType}
                    />
                );

            default:
                return (
                    <ListControls 
                        ControlsFrameworksOptions={ControlsFrameworksOptions}
                        controlType={controlType}
                        setControlType={setControlType}
                    />
                );
        }
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            {...navigatorOptions}
                            pageTitle={t("Controls")}
                        />
                    </Col>
                </Row>

                <PageBody>
                    <Tabs
                        t={t}
                    />

                    <div 
                        className="p-4 mb-5"
                    >
                        {renderTabContent()}
                    </div>
                </PageBody>
            </Container>
        </div>
    );
};

export default withNamespaces()(
    Controls
)
